/*
 * Recorte por comitente de los insumos de los tableros de saldos.
 *
 * El proveedor de saldos (`useSaldosClientes`) trae las cuentas que el usuario
 * puede leer; un comercial es personal interno y puede leer todas, pero su
 * Dashboard solo debe agregar los clientes que tiene asignados en el Sistema de
 * Gestión Comercial. Esto no es un límite de permisos: es el alcance de la vista.
 *
 * En todas las funciones, `scope === null` significa "sin recorte" y devuelve el
 * dato original (misma referencia), para no recalcular nada cuando no aplica.
 */

/** Set de comitentes normalizados a string, que es como vienen en los documentos. */
export const buildScope = (comitentes) => new Set((comitentes || []).map(String));

/** Clave estable de un recorte, para las dependencias de los useMemo. */
export const scopeKey = (comitentes) =>
    (comitentes ? [...comitentes].map(String).sort().join('|') : null);

/** Filtra una lista de documentos con `nroComitente`. */
export const filterByComitentes = (items, scope) => {
    if (!scope) return items;
    return (items || []).filter(item => scope.has(String(item?.nroComitente)));
};

/** Filtra un Map comitente → entry conservando el tipo Map. */
export const filterMapByComitentes = (map, scope) => {
    if (!scope) return map;
    const filtered = new Map();
    map.forEach((entry, nroComitente) => {
        if (scope.has(String(nroComitente))) filtered.set(nroComitente, entry);
    });
    return filtered;
};

/** Filtra los comitentes de un snapshot del custodio. */
export const filterSnapshotByComitentes = (snapshot, scope) => {
    if (!scope || !snapshot) return snapshot;
    return { ...snapshot, comitentes: filterByComitentes(snapshot.comitentes || [], scope) };
};

/**
 * Resuelve el alcance del Dashboard de Saldos a partir del rol y del padrón de
 * comerciales del SGC (`comercialesClientes`). Devuelve:
 *   - `null`                            → dashboard general, con todos los clientes.
 *   - `{ comitentes: [], isLoading: true }` → todavía no se sabe: la vista espera.
 *   - `{ comitentes, isLoading: false }` → recorte a esas cuentas.
 *
 * Un comercial que **no figura en el padrón** ve el general: puede no tener
 * usuario (mail) cargado en el SGC, tenerlo con otro dominio o no estar dado de
 * alta allá, y en ninguno de esos casos se puede saber cuáles son sus clientes.
 * Lo mismo si quedó inactivo (dejó de figurar como empleado comercial).
 *
 * Es lo contrario de lo que hace el padrón de productores, donde un doc inactivo
 * mantiene a la persona sin acceso a propósito. Acá no hay nada que proteger: el
 * comercial es staff y las reglas de Firestore lo dejan leer todo igual, así que
 * ante la duda se muestra la vista completa en vez de una vacía.
 */
export const resolveAlcanceDashboard = ({ esComercial, comercial, isLoading }) => {
    if (!esComercial) return null;
    if (isLoading) return { comitentes: [], isLoading: true };
    if (!comercial || comercial.active === false) return null;
    return { comitentes: comercial.comitentes || [], isLoading: false };
};

/**
 * "Mis clientes" de un comercial en las planillas (Operaciones, Cauciones/Pases,
 * Instrucciones, Licitaciones): sus cuentas del SGC más lo que cargó él mismo,
 * para no esconderle la orden de un cliente nuevo que todavía no tiene asignado.
 * Es un filtro de vista que el comercial apaga con "Toda la casa", no un permiso.
 *
 * `filtro` = { comitentes: Set, userId, userName } (ver buildFiltroMisClientes) o
 * null, que devuelve la lista original.
 */
export const buildFiltroMisClientes = ({ comitentes, userId = '', userName = '' }) => ({
    comitentes: buildScope(comitentes),
    userId,
    userName,
});

export const esDeMisClientes = (item, filtro) => (
    filtro.comitentes.has(String(item?.nroComitente))
    || (!!filtro.userId && item?.creadoPorId === filtro.userId)
    || (!!filtro.userName && item?.comercial === filtro.userName)
);

export const filterMisClientes = (items, filtro) => {
    if (!filtro) return items;
    return (items || []).filter(item => esDeMisClientes(item, filtro));
};
